import api from '@/api';
import type { components } from '@/api/qraft/schema';

export type StudentAdoptionInfo = components['schemas']['StudentAdoptionInfo'];

/**
 * Which ladder the endpoint runs before it answers. Omitted means `adopt`, the
 * server's own default; `assign` runs no adoption ladder at all (the target
 * helper is not chosen yet) but adds the caller's coordinator gate and narrows
 * the web-target list to groups the caller ADMINs
 * (`AssignStudentInteractivePopup.php:16-32,100-104`).
 */
export type AdoptionInfoMode = 'adopt' | 'assign';

export interface StudentAdoptionInfoResult {
  info: StudentAdoptionInfo | undefined;
  isLoading: boolean;
  /** A refusal, not a crash: see below. */
  error: unknown;
}

/**
 * The adopt / pass-on confirm payload for one student.
 *
 * `GET /v3/adoption/students/{id}/adoption-info` runs the adoption ladder before
 * it serves anything and answers a typed 4xx *instead of* the payload when the
 * caller may not adopt, so this query's `error` is as much a result as its
 * `data` — hence `retry: false`. Retrying a 403 three times with backoff would
 * leave the modal spinning for seconds on an answer it already had.
 *
 * @param studentId the student whose modal is open, or `null` when none is.
 * @param enabled false once the action has succeeded: the student is off the
 *   board by then, so a refetch would answer 404/409 and blank the success body.
 *   Disabling keeps the cached payload readable instead of re-reading it.
 * @param mode omitted for the adopt modal, `'assign'` for the assign one. The
 *   parameter is left off the request entirely when it is omitted, so the adopt
 *   modal's query key and URL are unchanged.
 */
export function useStudentAdoptionInfo(
  studentId: number | null,
  enabled: boolean,
  mode?: AdoptionInfoMode,
): StudentAdoptionInfoResult {
  const path = { student_id: studentId ?? 0 };
  const { data, isLoading, error } = api.getStudentAdoptionInfo.useQuery(
    mode === undefined ? { path } : { path, query: { mode } },
    { enabled: enabled && studentId !== null, retry: false },
  );

  return { info: data, isLoading, error };
}
